// ============================================================================
// Mon Temps d'Étude — Banque fin CE2 (entrée CM1)
// Production conforme spec v2.1 ratifiée par Ana le 19/09/2026
// Worktree: feature/lumen-diag-bank-2026-09
// Auteur: Lumen MTE <lumen@mte.local>
// Date production: 2026-09-19
// ============================================================================
//
// NOTE SCHÉMA: ce fichier étend le type union `BanqueItem` runtime pour
// supporter schoolLevel='CE2'. La définition runtime actuelle dans
// mte-shell/src/lib/banque-types.ts doit être élargie avant intégration.
//
// SOURCES OFFICIELLES (cf spec §5):
//   - Fluence CE2: ≥ 90 mots/min (BO n°41 du 31 octobre 2024 / ressources Eduscol CE2)
//   - Numération CE2: jusqu'à 10 000 (BO cycle 2 · Eduscol CE2)
//   - Autres seuils CE2: marqués RÈGLE INTERNE MTE faute de source officielle univoque CE2
// ============================================================================

import type { BanqueItem } from '../lib/banque-types';

type CE2Item = BanqueItem & { schoolLevel: 'CE2' };

const META_REGLE_INTERNE = { regle_interne_MTE: true, justification: "Pas de source primaire univoque CE2" };

const ITEMS_LECTURE_CODE: CE2Item[] = [
  {
    id: 'FR-CE2-LEC-01',
    schoolLevel: 'CE2',
    archetype: 'saisie',
    competence: 'FR-CE2-LEC-01',
    enonce: "Combien de syllabes a le mot 'rhinocéros' ? (réponse en chiffre)",
    bonneReponse: '5',
    tolerance: 0,
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO cycle 2 · CE2 · Lecture · lecture courante · mots longs',
      preuve_url: null,
      instruction_audio: { ce2: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'FR-CE2-LEC-02',
    schoolLevel: 'CE2',
    archetype: 'qcm',
    competence: 'FR-CE2-LEC-02',
    enonce: "Quel est le bon découpage syllabique du mot 'bibliothèque' ?",
    options: ['biblio-thè-que', 'bi-blio-thè-que', 'bibl-i-o-thèque', 'bib-li-o-tèque'],
    bonneReponse: 'bi-blio-thè-que',
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO cycle 2 · CE2 · Lecture · découpage syllabique',
      preuve_url: null,
      instruction_audio: { ce2: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'FR-CE2-LEC-03',
    schoolLevel: 'CE2',
    archetype: 'qcm',
    competence: 'FR-CE2-LEC-03',
    enonce: "Quel mot se prononce avec un son [j] ?",
    options: ['girafe', 'gâteau', 'jambe', 'guépard'],
    bonneReponse: 'jambe',
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO cycle 2 · CE2 · Lecture · discrimination sons',
      preuve_url: null,
      instruction_audio: { ce2: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'FR-CE2-LEC-04',
    schoolLevel: 'CE2',
    archetype: 'qcm',
    competence: 'FR-CE2-LEC-04',
    enonce: "Comment se prononce le 'g' dans 'gomme' ?",
    options: ['[g]', '[j]', '[k]', 'muet'],
    bonneReponse: '[g]',
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO cycle 2 · CE2 · Lecture · graphèmes complexes',
      preuve_url: null,
      instruction_audio: { ce2: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'FR-CE2-LEC-05',
    schoolLevel: 'CE2',
    archetype: 'qcm',
    competence: 'FR-CE2-LEC-05',
    enonce: "Quel mot est correctement écrit ?",
    options: ['écolier', 'écollier', 'école-ier', 'écolié'],
    bonneReponse: 'écolier',
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO cycle 2 · CE2 · Lecture · orthographe lexicale',
      preuve_url: null,
      instruction_audio: { ce2: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'FR-CE2-LEC-06',
    schoolLevel: 'CE2',
    archetype: 'saisie',
    competence: 'FR-CE2-LEC-06',
    enonce: "Écris le mot qui veut dire 'image fixe qui raconte une histoire'.",
    bonneReponse: 'bande dessinée',
    tolerance: 0,
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO cycle 2 · CE2 · Lecture · vocabulaire lié',
      preuve_url: null,
      instruction_audio: { ce2: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
];

const ITEMS_FLUENCE: CE2Item[] = [
  {
    id: 'FR-CE2-FLU-01',
    schoolLevel: 'CE2',
    archetype: 'lecture-voix-haute',
    competence: 'FR-CE2-FLU-01',
    enonce: 'Lis ce texte à voix haute pendant 1 minute.',
    textALire: "Le capitaine du bateau regarde l'horizon. La mer est calme ce matin. Les passagers sur le pont admirent les vagues. Soudain, un dauphin apparaît près du bateau. Les enfants crient de joie. Le dauphin saute plusieurs fois avant de disparaître.",
    dureeMaxSecondes: 60,
    evalMode: 'HUMAN',
    meta: {
      texte_usage: 'entrainement_fluence',
      titre: 'Le voyage en mer',
      mots_total: 53,
      source: 'mte_propre',
      auteur_licence: 'MTE — Equipe pédagogique 2026',
      niveau_progression: 'reference',
      source_officielle: 'BO n°41 du 31 octobre 2024 · CE2 · fluence · entrainement progressif',
      preuve_url: 'https://www.education.gouv.fr/bo/2024/Hebdo41/MENE2415135A',
      instruction_audio: { ce2: 'bouton' },
      stimulus_audio: { required: false },
      seuil_officiel: 90,
    },
  },
  {
    id: 'FR-CE2-FLU-02',
    schoolLevel: 'CE2',
    archetype: 'lecture-voix-haute',
    competence: 'FR-CE2-FLU-02',
    enonce: 'Lis ce texte à voix haute pendant 1 minute.',
    textALire: "Il était une fois un bûcheron qui avait sept enfants. Le plus jeune était si petit qu'on l'appelait le Petit Poucet. Un jour, ses parents ne pouvaient plus le nourrir. Ils le perdirent dans la forêt avec ses frères. Mais le Petit Poucet était très intelligent. Il sema des petits cailloux blancs pour retrouver le chemin.",
    dureeMaxSecondes: 60,
    evalMode: 'HUMAN',
    meta: {
      texte_usage: 'evaluation_1_minute',
      titre: "L'histoire du Petit Poucet",
      mots_total: 76,
      source: 'domaine_public',
      auteur_licence: 'Charles Perrault · domaine public (1697)',
      niveau_progression: 'reference',
      source_officielle: 'BO n°41 du 31 octobre 2024 · CE2 · fluence · cible ≥ 90 mots/min',
      preuve_url: 'https://www.education.gouv.fr/bo/2024/Hebdo41/MENE2415135A',
      instruction_audio: { ce2: 'bouton' },
      stimulus_audio: { required: false },
      seuil_officiel: 90,
    },
  },
  {
    id: 'FR-CE2-FLU-03',
    schoolLevel: 'CE2',
    archetype: 'lecture-voix-haute',
    competence: 'FR-CE2-FLU-03',
    enonce: 'Lis ce texte à voix haute pendant 1 minute.',
    textALire: "Au lever du jour, le fermier se rend à l'étable. Il nourrit les vaches, les moutons et les poules. Pendant ce temps, ses enfants préparent le petit-déjeuner. Après le repas, la famille va au marché pour vendre les fromages et les légumes. Le fermier discute avec ses voisins. La journée passe vite entre les travaux des champs et les soins aux animaux. À la tombée de la nuit, tout le monde rentre se reposer.",
    dureeMaxSecondes: 60,
    evalMode: 'HUMAN',
    meta: {
      texte_usage: 'evaluation_1_minute',
      titre: 'Une journée à la ferme',
      mots_total: 96,
      source: 'mte_propre',
      auteur_licence: 'MTE — Equipe pédagogique 2026',
      niveau_progression: 'reference',
      source_officielle: 'BO n°41 du 31 octobre 2024 · CE2 · fluence · cible ≥ 90 mots/min',
      preuve_url: 'https://www.education.gouv.fr/bo/2024/Hebdo41/MENE2415135A',
      instruction_audio: { ce2: 'bouton' },
      stimulus_audio: { required: false },
      seuil_officiel: 90,
    },
  },
];

const ITEMS_COMPREHENSION: CE2Item[] = [
  {
    id: 'FR-CE2-COMP-01',
    schoolLevel: 'CE2',
    archetype: 'qcm',
    competence: 'FR-CE2-COMP-01',
    enonce: "Lis : 'Camille attend son ami devant l'école. Elle le voit arriver avec un grand sourire. Ils se disent bonjour et entrent ensemble dans la cour.' Que font-ils ?",
    options: ['Ils se disputent', "Ils vont à l'école ensemble", 'Ils rentrent chez eux', 'Ils pleurent'],
    bonneReponse: "Ils vont à l'école ensemble",
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO cycle 2 · CE2 · Lecture · compréhension · qui fait quoi',
      preuve_url: null,
      instruction_audio: { ce2: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'FR-CE2-COMP-02',
    schoolLevel: 'CE2',
    archetype: 'qcm',
    competence: 'FR-CE2-COMP-02',
    enonce: "Lis : 'Au marché, Léa hésite entre deux pommes. La première est rouge et brillante. La deuxième est un peu abîmée mais plus grosse.' Laquelle Léa prend-elle ?",
    options: ['La pomme rouge et brillante', 'La pomme abîmée et plus grosse', 'Les deux', 'Aucune'],
    bonneReponse: 'La pomme abîmée et plus grosse',
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO cycle 2 · CE2 · Lecture · compréhension · inférence simple',
      preuve_url: null,
      instruction_audio: { ce2: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'FR-CE2-COMP-03',
    schoolLevel: 'CE2',
    archetype: 'qcm',
    competence: 'FR-CE2-COMP-03',
    enonce: "Lis : 'Il pleut très fort. Les rues sont vides. Le chat regarde par la fenêtre.' Quel temps ?",
    options: ['Il neige', 'Il pleut', 'Il fait soleil', 'Il fait froid'],
    bonneReponse: 'Il pleut',
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO cycle 2 · CE2 · Lecture · compréhension · informations explicites',
      preuve_url: null,
      instruction_audio: { ce2: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'FR-CE2-COMP-04',
    schoolLevel: 'CE2',
    archetype: 'saisie',
    competence: 'FR-CE2-COMP-04',
    enonce: "Lis : 'Le professeur distribue les cahiers aux élèves. Chaque élève remercie poliment. Ils ouvrent tous leur cahier à la même page.' Que fait le professeur en premier ?",
    consigne: "Réponds par un seul verbe (à l'infinitif).",
    bonneReponse: 'distribuer',
    tolerance: 1,
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO cycle 2 · CE2 · Lecture · compréhension · restitution',
      preuve_url: null,
      instruction_audio: { ce2: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'FR-CE2-COMP-05',
    schoolLevel: 'CE2',
    archetype: 'qcm',
    competence: 'FR-CE2-COMP-05',
    enonce: "Lis : 'Sara a lu trois livres cette semaine. Son préféré parle d'une petite fille qui voyage dans un pays lointain.' Quel genre ?",
    options: ['Un livre de cuisine', "Un livre d'aventure", 'Un livre de sciences', 'Un dictionnaire'],
    bonneReponse: "Un livre d'aventure",
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO cycle 2 · CE2 · Lecture · compréhension · inférence complexe',
      preuve_url: null,
      instruction_audio: { ce2: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'FR-CE2-COMP-06',
    schoolLevel: 'CE2',
    archetype: 'qcm',
    competence: 'FR-CE2-COMP-06',
    enonce: "Lis : 'Dans la forêt, le loup rencontre un petit lapin. Le lapin a très peur. Il se cache derrière un rocher.' Que ressent le lapin ?",
    options: ['De la joie', 'De la peur', 'De la faim', 'De la colère'],
    bonneReponse: 'De la peur',
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO cycle 2 · CE2 · Lecture · compréhension · émotions',
      preuve_url: null,
      instruction_audio: { ce2: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
];

const ITEMS_VOCABULAIRE: CE2Item[] = [
  {
    id: 'FR-CE2-VOC-01',
    schoolLevel: 'CE2',
    archetype: 'qcm',
    competence: 'FR-CE2-VOC-01',
    enonce: "Quel mot veut dire la même chose que 'rapide' ?",
    options: ['lent', 'vif', 'calme', 'tranquille'],
    bonneReponse: 'vif',
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO cycle 2 · CE2 · Vocabulaire · synonymes',
      preuve_url: null,
      instruction_audio: { ce2: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'FR-CE2-VOC-02',
    schoolLevel: 'CE2',
    archetype: 'qcm',
    competence: 'FR-CE2-VOC-02',
    enonce: "Quel mot appartient à la famille de 'blanc' ?",
    options: ['blanchir', 'blancheur', 'blanchâtre', 'toutes ces réponses'],
    bonneReponse: 'toutes ces réponses',
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO cycle 2 · CE2 · Vocabulaire · familles de mots',
      preuve_url: null,
      instruction_audio: { ce2: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'FR-CE2-VOC-03',
    schoolLevel: 'CE2',
    archetype: 'qcm',
    competence: 'FR-CE2-VOC-03',
    enonce: "Quel mot est un synonyme de 'content' ?",
    options: ['triste', 'fâché', 'heureux', 'fatigué'],
    bonneReponse: 'heureux',
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO cycle 2 · CE2 · Vocabulaire · synonymes',
      preuve_url: null,
      instruction_audio: { ce2: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'FR-CE2-VOC-04',
    schoolLevel: 'CE2',
    archetype: 'qcm',
    competence: 'FR-CE2-VOC-04',
    enonce: "Quel mot désigne une personne qui raconte des histoires ?",
    options: ['conteur', 'conteneur', 'cantonnier', 'cordonnier'],
    bonneReponse: 'conteur',
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO cycle 2 · CE2 · Vocabulaire · dérivation',
      preuve_url: null,
      instruction_audio: { ce2: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'FR-CE2-VOC-05',
    schoolLevel: 'CE2',
    archetype: 'saisie',
    competence: 'FR-CE2-VOC-05',
    enonce: "Trouve un mot de la famille de 'jardin'. Réponse en un mot.",
    bonneReponse: 'jardinier',
    tolerance: 0,
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO cycle 2 · CE2 · Vocabulaire · dérivation',
      preuve_url: null,
      instruction_audio: { ce2: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'FR-CE2-VOC-06',
    schoolLevel: 'CE2',
    archetype: 'qcm',
    competence: 'FR-CE2-VOC-06',
    enonce: "Quel mot est formé avec le préfixe 're-' ?",
    options: ['revenir', 'ramener', 'refaire', 'toutes ces réponses'],
    bonneReponse: 'toutes ces réponses',
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO cycle 2 · CE2 · Vocabulaire · préfixes',
      preuve_url: null,
      instruction_audio: { ce2: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
];

const ITEMS_ECRITURE_ORTHOGRAPHE: CE2Item[] = [
  {
    id: 'FR-CE2-ORTH-01',
    schoolLevel: 'CE2',
    archetype: 'qcm',
    competence: 'FR-CE2-ORTH-01',
    enonce: "Quel est le bon pluriel de 'journal' ?",
    options: ['des journaux', 'des journales', 'des journal', 'des journalx'],
    bonneReponse: 'des journaux',
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO cycle 2 · CE2 · Écriture · orthographe grammaticale · pluriels irréguliers',
      preuve_url: null,
      instruction_audio: { ce2: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'FR-CE2-ORTH-02',
    schoolLevel: 'CE2',
    archetype: 'saisie',
    competence: 'FR-CE2-ORTH-02',
    enonce: "Écris le pluriel de 'un animal' (plusieurs).",
    bonneReponse: 'des animaux',
    tolerance: 0,
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO cycle 2 · CE2 · Écriture · pluriel -al → -aux',
      preuve_url: null,
      instruction_audio: { ce2: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'FR-CE2-ORTH-03',
    schoolLevel: 'CE2',
    archetype: 'qcm',
    competence: 'FR-CE2-ORTH-03',
    enonce: "Quelle phrase est correcte ?",
    options: ['Les chat jouent', 'Les chats jouent', 'Le chats joue', 'Les chats jouentent'],
    bonneReponse: 'Les chats jouent',
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO cycle 2 · CE2 · Écriture · accord sujet-verbe initiation',
      preuve_url: null,
      instruction_audio: { ce2: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'FR-CE2-ORTH-04',
    schoolLevel: 'CE2',
    archetype: 'dictee',
    competence: 'FR-CE2-ORTH-04',
    enonce: 'Écris la phrase que tu entends.',
    audioUrl: '/assets/dictee/ce2/nous-allons-au-parc.wav',
    bonneReponse: 'Nous allons au parc.',
    replayMax: 3,
    evalMode: 'HUMAN',
    meta: {
      source_officielle: 'BO cycle 2 · CE2 · Écriture · dictée phrases',
      preuve_url: null,
      instruction_audio: { ce2: 'bouton' },
      stimulus_audio: { required: true, type: 'sound_discrimination' },
    },
  },
  {
    id: 'FR-CE2-ORTH-05',
    schoolLevel: 'CE2',
    archetype: 'qcm',
    competence: 'FR-CE2-ORTH-05',
    enonce: "Quel homophone complète : 'Il est ... de partir.'",
    options: ['prêt', 'pré', 'prêt ', 'prêt'],
    bonneReponse: 'prêt',
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO cycle 2 · CE2 · Écriture · homophones (prêt/pré)',
      preuve_url: null,
      instruction_audio: { ce2: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
];

const ITEMS_CONSIGNES: CE2Item[] = [
  {
    id: 'FR-CE2-CONS-01',
    schoolLevel: 'CE2',
    archetype: 'qcm',
    competence: 'FR-CE2-CONS-01',
    enonce: 'Consigne : "Range ces nombres du plus petit au plus grand". Que faire ?',
    options: ['Les additionner', 'Les ordonner', 'Les multiplier', 'Les effacer'],
    bonneReponse: 'Les ordonner',
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO cycle 2 · CE2 · Consigne mathématique',
      preuve_url: null,
      instruction_audio: { ce2: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'FR-CE2-CONS-02',
    schoolLevel: 'CE2',
    archetype: 'qcm',
    competence: 'FR-CE2-CONS-02',
    enonce: 'Consigne : "Souligne les verbes en rouge et les noms en bleu". Combien de couleurs ?',
    options: ['1', '2', '3', '4'],
    bonneReponse: '2',
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO cycle 2 · CE2 · Consigne 2 temps + 2 couleurs',
      preuve_url: null,
      instruction_audio: { ce2: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'FR-CE2-CONS-03',
    schoolLevel: 'CE2',
    archetype: 'qcm',
    competence: 'FR-CE2-CONS-03',
    enonce: 'Consigne : "Observe le tableau puis écris ta réponse sur ton cahier". Que fais-tu en premier ?',
    options: ['Écrire sur le cahier', 'Observer le tableau', 'Effacer le tableau', 'Fermer le cahier'],
    bonneReponse: 'Observer le tableau',
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO cycle 2 · CE2 · Consigne 2 temps ordonnée',
      preuve_url: null,
      instruction_audio: { ce2: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
];

const ITEMS_NUMERATION: CE2Item[] = [
  {
    id: 'MA-CE2-NB-01',
    schoolLevel: 'CE2',
    archetype: 'qcm',
    competence: 'MA-CE2-NB-01',
    enonce: 'Quel nombre est le plus grand ?',
    options: ['2 345', '3 245', '2 543', '2 435'],
    bonneReponse: '3 245',
    evalMode: 'AUTO',
    meta: {
      source_officielle: "BO cycle 2 · CE2 · Mathématiques · comparer jusqu'à 10 000",
      preuve_url: null,
      champ_officiel: 'nombres jusqu\u2019à 10 000',
      instruction_audio: { ce2: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'MA-CE2-NB-02',
    schoolLevel: 'CE2',
    archetype: 'saisie',
    competence: 'MA-CE2-NB-02',
    enonce: 'Écris le nombre cinq mille deux cent trente-quatre en chiffres.',
    bonneReponse: '5234',
    tolerance: 0,
    evalMode: 'AUTO',
    meta: {
      source_officielle: "BO cycle 2 · CE2 · Mathématiques · lire/écrire jusqu'à 10 000",
      preuve_url: null,
      champ_officiel: 'nombres jusqu\u2019à 10 000',
      instruction_audio: { ce2: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'MA-CE2-NB-03',
    schoolLevel: 'CE2',
    archetype: 'qcm',
    competence: 'MA-CE2-NB-03',
    enonce: 'Combien de centaines dans 4 700 ?',
    options: ['4', '47', '470', '4 700'],
    bonneReponse: '47',
    evalMode: 'AUTO',
    meta: {
      source_officielle: "BO cycle 2 · CE2 · Mathématiques · valeur positionnelle",
      preuve_url: null,
      champ_officiel: 'nombres jusqu\u2019à 10 000',
      instruction_audio: { ce2: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'MA-CE2-NB-04',
    schoolLevel: 'CE2',
    archetype: 'qcm',
    competence: 'MA-CE2-NB-04',
    enonce: 'Range ces nombres du plus petit au plus grand.',
    options: ['3 890 < 3 980 < 4 890', '3 980 < 3 890 < 4 890', '4 890 < 3 980 < 3 890', '3 890 < 4 890 < 3 980'],
    bonneReponse: '3 890 < 3 980 < 4 890',
    evalMode: 'AUTO',
    meta: {
      source_officielle: "BO cycle 2 · CE2 · Mathématiques · ranger jusqu'à 10 000",
      preuve_url: null,
      champ_officiel: 'nombres jusqu\u2019à 10 000',
      instruction_audio: { ce2: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'MA-CE2-NB-05',
    schoolLevel: 'CE2',
    archetype: 'saisie',
    competence: 'MA-CE2-NB-05',
    enonce: 'Complète : 6 432 = 6 000 + 400 + 30 + ___ .',
    bonneReponse: '2',
    tolerance: 0,
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO cycle 2 · CE2 · Mathématiques · décomposition milliers',
      preuve_url: null,
      champ_officiel: 'nombres jusqu\u2019à 10 000',
      instruction_audio: { ce2: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'MA-CE2-NB-06',
    schoolLevel: 'CE2',
    archetype: 'qcm',
    competence: 'MA-CE2-NB-06',
    enonce: "Quel nombre est juste après 5 999 ?",
    options: ['6 000', '5 990', '6 100', '5 899'],
    bonneReponse: '6 000',
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO cycle 2 · CE2 · Mathématiques · successeur',
      preuve_url: null,
      champ_officiel: 'nombres jusqu\u2019à 10 000',
      instruction_audio: { ce2: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
];

const ITEMS_CALCUL_MENTAL: CE2Item[] = [
  {
    id: 'MA-CE2-CAL-01',
    schoolLevel: 'CE2',
    archetype: 'saisie',
    competence: 'MA-CE2-CAL-01',
    enonce: 'Combien font 7 × 6 ?',
    bonneReponse: '42',
    tolerance: 0,
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO cycle 2 · CE2 · Mathématiques · tables multiplication',
      preuve_url: null,
      ...META_REGLE_INTERNE,
      instruction_audio: { ce2: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'MA-CE2-CAL-02',
    schoolLevel: 'CE2',
    archetype: 'saisie',
    competence: 'MA-CE2-CAL-02',
    enonce: 'Combien font 8 × 7 ?',
    bonneReponse: '56',
    tolerance: 0,
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO cycle 2 · CE2 · Mathématiques · tables multiplication',
      preuve_url: null,
      ...META_REGLE_INTERNE,
      instruction_audio: { ce2: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'MA-CE2-CAL-03',
    schoolLevel: 'CE2',
    archetype: 'vf',
    competence: 'MA-CE2-CAL-03',
    enonce: '9 × 9 = 81. Vrai ou faux ?',
    bonneReponse: true,
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO cycle 2 · CE2 · Mathématiques · tables multiplication',
      preuve_url: null,
      instruction_audio: { ce2: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'MA-CE2-CAL-04',
    schoolLevel: 'CE2',
    archetype: 'saisie',
    competence: 'MA-CE2-CAL-04',
    enonce: 'Combien font 45 + 27 ? (calcul mental)',
    bonneReponse: '72',
    tolerance: 0,
    evalMode: 'AUTO',
    meta: {
      source_officielle: "BO cycle 2 · CE2 · Mathématiques · addition mentale jusqu'à 100",
      preuve_url: null,
      ...META_REGLE_INTERNE,
      instruction_audio: { ce2: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'MA-CE2-CAL-05',
    schoolLevel: 'CE2',
    archetype: 'saisie',
    competence: 'MA-CE2-CAL-05',
    enonce: 'Combien font 100 - 37 ? (calcul mental)',
    bonneReponse: '63',
    tolerance: 0,
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO cycle 2 · CE2 · Mathématiques · soustraction mentale',
      preuve_url: null,
      ...META_REGLE_INTERNE,
      instruction_audio: { ce2: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
];

const ITEMS_OPERATIONS: CE2Item[] = [
  {
    id: 'MA-CE2-OP-01',
    schoolLevel: 'CE2',
    archetype: 'saisie',
    competence: 'MA-CE2-OP-01',
    enonce: "Pose l'opération 234 + 158. Quel est le résultat ?",
    bonneReponse: '392',
    tolerance: 0,
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO cycle 2 · CE2 · Mathématiques · addition posée avec retenue',
      preuve_url: null,
      ...META_REGLE_INTERNE,
      instruction_audio: { ce2: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'MA-CE2-OP-02',
    schoolLevel: 'CE2',
    archetype: 'saisie',
    competence: 'MA-CE2-OP-02',
    enonce: "Pose l'opération 467 - 239. Quel est le résultat ?",
    bonneReponse: '228',
    tolerance: 0,
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO cycle 2 · CE2 · Mathématiques · soustraction posée',
      preuve_url: null,
      ...META_REGLE_INTERNE,
      instruction_audio: { ce2: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'MA-CE2-OP-03',
    schoolLevel: 'CE2',
    archetype: 'manipulation-glisser',
    competence: 'MA-CE2-OP-03',
    enonce: "Pose l'opération 52 × 4. Quel est le résultat ?",
    spec: { operation: '52*4', format: 'colonnes' },
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO cycle 2 · CE2 · Mathématiques · multiplication posée',
      preuve_url: null,
      ...META_REGLE_INTERNE,
      instruction_audio: { ce2: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'MA-CE2-OP-04',
    schoolLevel: 'CE2',
    archetype: 'saisie',
    competence: 'MA-CE2-OP-04',
    enonce: "Pose l'opération 875 - 469. Quel est le résultat ?",
    bonneReponse: '406',
    tolerance: 0,
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO cycle 2 · CE2 · Mathématiques · soustraction posée',
      preuve_url: null,
      ...META_REGLE_INTERNE,
      instruction_audio: { ce2: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
];

const ITEMS_PROBLEMES: CE2Item[] = [
  {
    id: 'MA-CE2-PROB-01',
    schoolLevel: 'CE2',
    archetype: 'texte-a-trou',
    competence: 'MA-CE2-PROB-01',
    enonce: "Pierre a 45 billes. Il en perd 12 puis en gagne 18. Combien maintenant ?",
    enonceSegments: ['Pierre a 45 billes. Il en perd 12 puis en gagne 18. Combien maintenant ? Réponse : ', '{T1}'],
    trous: [{ id: 'T1', bonneReponse: '51', tolerance: 0 }],
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO cycle 2 · CE2 · Mathématiques · Résolution · 2 étapes',
      preuve_url: null,
      instruction_audio: { ce2: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'MA-CE2-PROB-02',
    schoolLevel: 'CE2',
    archetype: 'saisie',
    competence: 'MA-CE2-PROB-02',
    enonce: "Un jardinier cueille 156 fleurs le matin et 87 l'après-midi. Il en vend 124. Combien lui reste-t-il ?",
    bonneReponse: '119',
    tolerance: 0,
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO cycle 2 · CE2 · Mathématiques · Résolution · 2 étapes',
      preuve_url: null,
      ...META_REGLE_INTERNE,
      instruction_audio: { ce2: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'MA-CE2-PROB-03',
    schoolLevel: 'CE2',
    archetype: 'qcm',
    competence: 'MA-CE2-PROB-03',
    enonce: "Une école compte 235 élèves en CE2 et 198 en CM1. Combien d'élèves en plus au CE2 qu'au CM1 ?",
    options: ['37', '433', '63', '137'],
    bonneReponse: '37',
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO cycle 2 · CE2 · Mathématiques · Résolution · comparaison',
      preuve_url: null,
      ...META_REGLE_INTERNE,
      instruction_audio: { ce2: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
  {
    id: 'MA-CE2-PROB-04',
    schoolLevel: 'CE2',
    archetype: 'saisie',
    competence: 'MA-CE2-PROB-04',
    enonce: "Un libraire reçoit 240 livres. Le matin il en vend 84. L'après-midi 56. Combien lui reste-t-il ?",
    bonneReponse: '100',
    tolerance: 0,
    evalMode: 'AUTO',
    meta: {
      source_officielle: 'BO cycle 2 · CE2 · Mathématiques · Résolution · 2 étapes',
      preuve_url: null,
      ...META_REGLE_INTERNE,
      instruction_audio: { ce2: 'bouton' },
      stimulus_audio: { required: false },
    },
  },
];

export const BANQUE_CE2: CE2Item[] = [
  ...ITEMS_LECTURE_CODE,
  ...ITEMS_FLUENCE,
  ...ITEMS_COMPREHENSION,
  ...ITEMS_VOCABULAIRE,
  ...ITEMS_ECRITURE_ORTHOGRAPHE,
  ...ITEMS_CONSIGNES,
  ...ITEMS_NUMERATION,
  ...ITEMS_CALCUL_MENTAL,
  ...ITEMS_OPERATIONS,
  ...ITEMS_PROBLEMES,
];

export const BANQUE_CE2_COUNT = BANQUE_CE2.length;
export const BANQUE_CE2_REPARTITION = {
  lecture_code: ITEMS_LECTURE_CODE.length,
  fluence: ITEMS_FLUENCE.length,
  comprehension: ITEMS_COMPREHENSION.length,
  vocabulaire: ITEMS_VOCABULAIRE.length,
  ecriture_orthographe: ITEMS_ECRITURE_ORTHOGRAPHE.length,
  consignes: ITEMS_CONSIGNES.length,
  numeration: ITEMS_NUMERATION.length,
  calcul_mental: ITEMS_CALCUL_MENTAL.length,
  operations: ITEMS_OPERATIONS.length,
  problemes: ITEMS_PROBLEMES.length,
};
